/**
 * 全局音频播放管理器
 * 基于原生 HTML5 Audio API 封装，提供统一的音频播放控制接口
 */

class AudioManager {
  constructor() {
    // 当前音频实例（HTMLAudioElement）
    this.currentAudio = null;

    // 当前音频信息
    this.currentTrack = null;

    // 播放状态
    this.isPlaying = false;
    this.isLoading = false;
    // 当前正在进行的 play() Promise，用于处理 AbortError
    this.playPromise = null;

    // 事件回调
    this.callbacks = {
      onPlay: null, // 开始播放
      onPause: null, // 暂停
      onStop: null, // 停止
      onEnd: null, // 播放结束
      onLoad: null, // 加载完成
      onLoadError: null, // 加载错误
      onLoadingChange: null, // 加载状态变化
      onSeek: null, // 跳转
      onProgress: null, // 播放进度更新
      onPlayError: null, // 播放错误
      onUrlExpired: null // URL过期回调，用于刷新URL
    };

    // 进度更新定时器
    this.progressTimer = null;

    // 事件处理器引用，用于清理
    this._eventHandlers = {};

    // URL 加载时间追踪（用于判断是否需要预检测）
    this.urlLoadTime = null;
    // URL 预检测的 AbortController（用于取消正在进行的检测）
    this.urlCheckAbortController = null;
  }

  /**
   * 设置加载状态并触发事件
   * @param {boolean} loading - 加载状态
   * @private
   */
  setLoadingState(loading) {
    if (this.isLoading !== loading) {
      this.isLoading = loading;
      console.log("[AudioManager] Loading 状态变化:", loading);

      if (this.callbacks.onLoadingChange) {
        this.callbacks.onLoadingChange({ isLoading: loading });
      }
    }
  }

  /**
   * 加载音频
   * @param {Object} track - 音轨对象，包含 { id, url, title, ... }
   * @returns {Promise<void>}
   */
  load(track) {
    return new Promise((resolve, reject) => {
      // 如果有正在播放的音频，先停止并销毁
      if (this.currentAudio) {
        this.destroy();
      }

      // 保存音轨信息
      this.currentTrack = track;
      this.setLoadingState(true);
      // 记录 URL 加载时间
      this.urlLoadTime = Date.now();

      console.log("[AudioManager] 加载音频:", track.title || track.url);

      // 创建新的 Audio 实例
      this.currentAudio = new Audio(track.url);

      // 设置事件处理器
      this.setupAudioEventHandlers(resolve, reject);
    });
  }

  /**
   * 设置音频事件处理器
   * @param {Function} resolve - Promise resolve
   * @param {Function} reject - Promise reject
   * @private
   */
  setupAudioEventHandlers(resolve, reject) {
    const audio = this.currentAudio;

    // 可以开始播放（加载完成）
    this._eventHandlers.canplay = () => {
      console.log("[AudioManager] 音频加载完成");
      this.setLoadingState(false);

      if (this.callbacks.onLoad) {
        this.callbacks.onLoad({
          duration: this.getDuration()
        });
      }

      resolve();
    };

    // 加载错误（统一处理所有错误，包括403等）
    this._eventHandlers.error = () => {
      const error = audio.error;
      let errorCode = 0;

      if (error) {
        // MediaError 错误码映射
        // MEDIA_ERR_ABORTED = 1
        // MEDIA_ERR_NETWORK = 2
        // MEDIA_ERR_DECODE = 3
        // MEDIA_ERR_SRC_NOT_SUPPORTED = 4
        errorCode = error.code;
        console.error("[AudioManager] 音频加载失败:", {
          code: error.code,
          message: error.message
        });

        // 检测URL过期（403等网络错误）
        if (
          error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
          error.code === MediaError.MEDIA_ERR_NETWORK
        ) {
          console.warn(
            "[AudioManager] 检测到可能的URL过期（403），触发URL刷新"
          );

          // 如果有URL过期回调，触发它
          if (this.callbacks.onUrlExpired) {
            this.callbacks.onUrlExpired();
          }
        }
      } else {
        console.error("[AudioManager] 音频加载失败: 未知错误");
        errorCode = 2; // 默认网络错误
      }

      this.setLoadingState(false);

      if (this.callbacks.onLoadError) {
        this.callbacks.onLoadError(errorCode);
      }

      reject(errorCode);
    };

    // 播放
    this._eventHandlers.play = () => {
      console.log("[AudioManager] 开始播放");
      this.isPlaying = true;

      // 开始进度更新
      this.startProgressTimer();

      if (this.callbacks.onPlay) {
        this.callbacks.onPlay();
      }
    };

    // 暂停
    this._eventHandlers.pause = () => {
      console.log("[AudioManager] 暂停播放");
      this.isPlaying = false;

      // 停止进度更新
      this.stopProgressTimer();

      if (this.callbacks.onPause) {
        this.callbacks.onPause();
      }
    };

    // 播放结束
    this._eventHandlers.ended = () => {
      console.log("[AudioManager] 播放结束");
      this.isPlaying = false;

      // 停止进度更新
      this.stopProgressTimer();

      if (this.callbacks.onEnd) {
        this.callbacks.onEnd();
      }
    };

    // 跳转
    this._eventHandlers.seeking = () => {
      console.log("[AudioManager] 跳转位置");

      if (this.callbacks.onSeek) {
        this.callbacks.onSeek({
          currentTime: this.getCurrentTime()
        });
      }
    };

    // 绑定所有事件
    audio.addEventListener("canplay", this._eventHandlers.canplay);
    audio.addEventListener("error", this._eventHandlers.error);
    audio.addEventListener("play", this._eventHandlers.play);
    audio.addEventListener("pause", this._eventHandlers.pause);
    audio.addEventListener("ended", this._eventHandlers.ended);
    audio.addEventListener("seeking", this._eventHandlers.seeking);
  }

  /**
   * 从URL中提取时间戳参数
   * @param {string} url - 音频URL
   * @returns {number|null} 返回时间戳，如果不存在则返回null
   * @private
   */
  extractTimestampFromUrl(url) {
    try {
      const urlObj = new URL(url);
      const timestamp = urlObj.searchParams.get("timestamp");
      return timestamp ? parseInt(timestamp, 10) : null;
    } catch (error) {
      console.warn("[AudioManager] 解析URL时间戳失败:", error);
      return null;
    }
  }

  /**
   * 检查 URL 是否过期（基于时间戳或加载时间）
   * @param {string} url - 音频URL
   * @param {number} urlLoadTime - URL 加载时间（毫秒时间戳）
   * @returns {boolean} true 表示可能过期，需要预检测；false 表示未过期
   * @private
   */
  isUrlLikelyExpired(url, urlLoadTime) {
    if (!url || !urlLoadTime) {
      return false;
    }

    // 优先使用 URL 中的时间戳判断
    const urlTimestamp = this.extractTimestampFromUrl(url);
    if (urlTimestamp) {
      // URL 中有时间戳，判断是否接近或超过 10 秒有效期
      // 在 8 秒时就提前检测，避免在过期边缘才刷新（留2秒缓冲）
      const now = Math.floor(Date.now() / 1000); // 转换为秒
      const elapsed = now - urlTimestamp;
      const shouldCheck = elapsed >= 8; // 8秒后就需要预检测（有效期10秒）

      if (shouldCheck) {
        console.log(
          `[AudioManager] URL 时间戳接近过期: ${elapsed}秒 (有效期10秒，提前2秒检测)`
        );
        return true;
      } else {
        console.log(
          `[AudioManager] URL 时间戳仍有效: ${elapsed}秒 (有效期10秒)`
        );
        return false;
      }
    }

    // 如果没有时间戳，使用加载时间差判断
    // 如果距离加载时间超过 8 秒，认为可能过期（保守估计，留2秒缓冲）
    const elapsed = Date.now() - urlLoadTime;
    const shouldCheck = elapsed > 8000; // 8秒后需要检测

    if (shouldCheck) {
      console.log(
        `[AudioManager] URL 加载时间已超过8秒: ${Math.floor(elapsed / 1000)}秒，需要预检测`
      );
      return true;
    }

    return false;
  }
  /**
   * 播放音频
   * 在播放前会预检测 URL 是否过期，避免 HTML5 Audio 内部不断重试
   */
  async play() {
    if (!this.currentAudio) {
      console.warn("[AudioManager] 没有加载的音频");
      return;
    }

    if (this.isLoading) {
      console.warn("[AudioManager] 音频正在加载中");
      return;
    }

    // 在播放前检查 URL 是否过期（基于时间戳）
    // 这样可以避免 HTML5 Audio 在恢复播放时自动预加载过期 URL 导致的重试问题
    const isExpired =
      this.urlLoadTime &&
      this.currentTrack &&
      this.currentTrack.url &&
      this.isUrlLikelyExpired(this.currentTrack.url, this.urlLoadTime);

    if (isExpired) {
      // URL 已过期，直接触发 URL 过期回调，让外部刷新 URL
      console.warn(
        "[AudioManager] URL 已过期（基于时间戳判断），触发 URL 刷新回调"
      );

      if (this.callbacks.onUrlExpired) {
        // 先暂停当前音频，避免继续加载
        // 如果正在播放，先处理 playPromise
        if (this.playPromise) {
          // 捕获可能的 AbortError（这是预期的）
          this.playPromise.catch(() => {
            // 忽略 AbortError，这是预期的
          });
          this.playPromise = null;
        }
        this.currentAudio.pause();
        // 触发 URL 过期回调
        this.callbacks.onUrlExpired();
      }

      // 不继续播放，等待外部刷新 URL 后重新加载
      return;
    }

    // 正常播放
    // play() 方法返回 Promise，处理播放失败的情况
    // 保存 playPromise 以便在 pause() 时正确处理 AbortError
    this.playPromise = this.currentAudio.play();

    this.playPromise
      .then(() => {
        // 播放成功
        this.playPromise = null;
      })
      .catch(error => {
        // 清除 playPromise
        this.playPromise = null;

        // AbortError 是预期的，当 pause() 在 play() 完成前被调用时会发生
        // 这种情况下不应该触发错误回调
        if (error.name === "AbortError") {
          console.log("[AudioManager] 播放被暂停中断（这是正常的）");
          return;
        }

        // 其他错误才需要处理
        console.error("[AudioManager] 播放失败:", error);
        this.isPlaying = false;
        if (this.callbacks.onPlayError) {
          this.callbacks.onPlayError(2);
        }
      });
  }

  /**
   * 暂停播放
   */
  pause() {
    if (!this.currentAudio) {
      return;
    }

    // 如果正在等待 play() Promise，先等待它完成或处理 AbortError
    // 这样可以避免 AbortError 被当作真正的错误
    if (this.playPromise) {
      // pause() 会中断 play() Promise，导致 AbortError
      // 这是预期的行为，我们在这里捕获它
      this.playPromise.catch(error => {
        // AbortError 是预期的，不需要处理
        if (error.name !== "AbortError") {
          console.error("[AudioManager] 暂停时发生错误:", error);
        }
      });
      this.playPromise = null;
    }

    this.currentAudio.pause();
  }

  /**
   * 停止播放
   */
  stop() {
    if (!this.currentAudio) {
      return;
    }

    // 如果正在等待 play() Promise，先处理它
    if (this.playPromise) {
      this.playPromise.catch(error => {
        // AbortError 是预期的，不需要处理
        if (error.name !== "AbortError") {
          console.error("[AudioManager] 停止时发生错误:", error);
        }
      });
      this.playPromise = null;
    }

    this.currentAudio.pause();
    this.currentAudio.currentTime = 0;
    this.isPlaying = false;

    // 停止进度更新
    this.stopProgressTimer();

    if (this.callbacks.onStop) {
      this.callbacks.onStop();
    }
  }

  /**
   * 切换播放/暂停状态
   */
  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * 跳转到指定时间（秒）
   * @param {number} time - 跳转的时间位置（秒）
   */
  seek(time) {
    if (!this.currentAudio) {
      return;
    }

    const duration = this.getDuration();

    // 确保时间在有效范围内
    const seekTime = Math.max(0, Math.min(time, duration));

    this.currentAudio.currentTime = seekTime;
  }

  /**
   * 获取当前播放时间（秒）
   * @returns {number}
   */
  getCurrentTime() {
    if (!this.currentAudio) {
      return 0;
    }

    return this.currentAudio.currentTime || 0;
  }

  /**
   * 获取音频总时长（秒）
   * @returns {number}
   */
  getDuration() {
    if (!this.currentAudio) {
      return 0;
    }

    return this.currentAudio.duration || 0;
  }

  /**
   * 获取当前音轨信息
   * @returns {Object|null}
   */
  getCurrentTrack() {
    return this.currentTrack;
  }

  /**
   * 获取播放状态
   * @returns {boolean}
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * 获取加载状态
   * @returns {boolean}
   */
  getIsLoading() {
    return this.isLoading;
  }

  /**
   * 销毁当前音频实例
   */
  destroy() {
    // 移除所有事件监听器
    if (this.currentAudio) {
      // 如果正在等待 play() Promise，先处理它
      if (this.playPromise) {
        this.playPromise.catch(() => {
          // 忽略所有错误，包括 AbortError
        });
        this.playPromise = null;
      }

      // 移除所有事件监听器
      if (this._eventHandlers) {
        Object.keys(this._eventHandlers).forEach(eventName => {
          if (this._eventHandlers[eventName]) {
            this.currentAudio.removeEventListener(
              eventName,
              this._eventHandlers[eventName]
            );
          }
        });
        this._eventHandlers = {};
      }

      this.stopProgressTimer();
      this.currentAudio.pause();
      this.currentAudio.src = "";
      this.currentAudio.load(); // 重置音频元素
      this.currentAudio = null;
    }

    this.currentTrack = null;
    this.isPlaying = false;
    this.setLoadingState(false);
  }

  /**
   * 设置事件回调
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  on(event, callback) {
    const eventKey = `on${event.charAt(0).toUpperCase()}${event.slice(1)}`;

    if (Object.prototype.hasOwnProperty.call(this.callbacks, eventKey)) {
      this.callbacks[eventKey] = callback;
    } else {
      console.warn(`[AudioManager] 未知的事件类型: ${event}`);
    }
  }

  /**
   * 移除事件回调
   * @param {string} event - 事件名称
   */
  off(event) {
    const eventKey = `on${event.charAt(0).toUpperCase()}${event.slice(1)}`;

    if (Object.prototype.hasOwnProperty.call(this.callbacks, eventKey)) {
      this.callbacks[eventKey] = null;
    }
  }

  /**
   * 开始进度更新定时器
   * @private
   */
  startProgressTimer() {
    // 清除现有定时器
    this.stopProgressTimer();

    // 每秒更新一次进度
    this.progressTimer = setInterval(() => {
      if (this.isPlaying && this.callbacks.onProgress) {
        this.callbacks.onProgress({
          currentTime: this.getCurrentTime(),
          duration: this.getDuration()
        });
      }
    }, 1000);
  }

  /**
   * 停止进度更新定时器
   * @private
   */
  stopProgressTimer() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  /**
   * 获取播放进度百分比
   * @returns {number} 0-100
   */
  getProgress() {
    const duration = this.getDuration();

    if (duration === 0) {
      return 0;
    }

    return (this.getCurrentTime() / duration) * 100;
  }
}

// 创建并导出单例实例
const audioManager = new AudioManager();

export default audioManager;

// 同时导出类，便于测试或创建多个实例
export { AudioManager };
